import clsx from 'clsx';
import React from 'react';

import ArticleFeedItem from '@components/FeedItems/ArticleFeedItem';
import ArticleHeader from '@components/FeedItems/ArticleHeader';
import ArticleImage from '@components/FeedItems/ArticleImage';
import useEventTracking from '@hooks/useEventTracking';
import { TrackingLocation } from '@tracking';

import { Article } from '../../interfaces/homepage';
import Byline from '../Byline';
import Highlight from './Highlight';
import { Page } from './TopArticlesSection';

interface SideArticleProps {
  article: Article;
  imageClassName?: string;
  page: Page;
  articleIndex: number;
}

const SideArticle = ({
  article: { authors, highlight, id, isPro, publishedAt, picture, slug, title },
  imageClassName,
  page,
  articleIndex,
}: SideArticleProps): JSX.Element => {
  const { Track } = useEventTracking({ location: TrackingLocation.TopArticlesSide });

  return (
    <Track>
      <ArticleFeedItem
        articleId={id}
        articleAuthors={authors}
        contentIndex={articleIndex}
        className={clsx(
          'flex-col px-16 even:mt-32 even:border-0 even:border-solid even:border-grayscale-gray even:pt-32 sm:px-0',
          {
            'even:border-t sm:flex-row md:flex-col md:pt-0 md:even:mt-24 md:even:pt-24':
              page === Page.Highlight,
            'even:border-t sm:even:mt-56 sm:even:border-t-0 sm:even:pt-0': page === Page.Home,
          }
        )}
      >
        <ArticleImage
          className={imageClassName}
          id={id}
          isMobileFullWidth
          isPro={isPro}
          picture={picture}
          sizes="(min-width: 768px) 250px, (min-width: 480px) 600px, 400px"
          slug={slug}
          title={title}
        />
        <div
          className={clsx('ml-0 mt-16', {
            'sm:ml-24 sm:mt-0 md:ml-0 md:mt-24': page === Page.Highlight,
            'sm:mt-24': page === Page.Home,
          })}
        >
          {highlight && <Highlight highlight={highlight} />}
          <ArticleHeader
            className="text-headline-xs md:text-headline-xxs"
            title={title}
            slug={slug}
          />
          <Byline authors={authors} publishedAt={publishedAt} />
        </div>
      </ArticleFeedItem>
    </Track>
  );
};

export default SideArticle;
